/**
 * Turn any FastAPI error body into a string a human can read.
 *
 * WHY THIS EXISTS. `toast.error(err.response?.data?.detail)` blanked the whole
 * Create Tenant page. FastAPI's `detail` is a STRING for `HTTPException` but an
 * ARRAY of `{type, loc, msg, input, ctx}` objects for a 422, and React refuses
 * to render an object as a child — error #31, thrown during render, which
 * unmounts the tree. So an operator who mistyped an email address got a white
 * screen instead of "invalid email address".
 *
 * The failure is worse than it looks: the page dies AFTER the request, so there
 * is no message, no form, and no way to tell whether the tenant was created.
 *
 * Handles the four shapes this API actually returns:
 *   "a string"                                  -> itself
 *   {error, message}                            -> message
 *   [{loc, msg}, ...]                           -> "field: message" per line
 *   anything else                               -> the fallback, never [object Object]
 */
export function describeApiError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const lines = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        // `loc` is ["body", "admin_email"] — the leading "body" is noise to an
        // operator, so it is dropped and the field name kept.
        const field = Array.isArray(d?.loc)
          ? d.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = d?.msg || d?.message || '';
        return field && msg ? `${field}: ${msg}` : msg || '';
      })
      .filter(Boolean);
    if (lines.length) return lines.join('\n');
  }

  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string' && detail.message) return detail.message;
    if (typeof detail.error === 'string' && detail.error) return detail.error;
  }

  // A request that never got a response is UNKNOWN, not failed — the work may
  // have completed. Admin runs a single uvicorn worker, so a long handler can
  // stall the process and the proxy answers 503 with no CORS headers, leaving
  // no response object at all.
  if (err && !err.response) {
    return 'No response from the server, so this could not be confirmed either '
         + 'way. Re-read the page before retrying — it may already have worked.';
  }

  return fallback;
}
